import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Award, Clock, DollarSign, TrendingDown, TrendingUp, Minus } from 'lucide-react';

/** 2x2 grid of secondary model-reference stats (reference effort, day-cost, audit days, upkeep). */
export function KeyStatsGrid({ comparison, industryLabel, currencySymbol, formatCost }) {
  const { t } = useTranslation();
  const overallTone = comparison.effort.status.tone;

  let TrendIcon = Minus;
  if (overallTone === 'positive') TrendIcon = TrendingDown;
  else if (overallTone === 'warning') TrendIcon = TrendingUp;

  return (
    <div className="grid grid-cols-2 gap-4">
      <Card className="bg-white border-[#DDE1E5]">
        <CardContent className="pt-5 pb-4 px-4">
          <div className="flex items-center gap-2 text-xs text-[#66707A] uppercase tracking-wider">
            <Award className="w-3.5 h-3.5" />
            {t('benchmark.referenceEffort')}
          </div>
          <div
            className="mt-2 text-2xl font-semibold text-[#171A1D]"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            data-testid="benchmark-reference-effort"
          >
            {comparison.effort.avg}d
          </div>
          <p className="text-xs text-[#8A929B] mt-1">{industryLabel} {t('benchmark.average')}</p>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#DDE1E5]">
        <CardContent className="pt-5 pb-4 px-4">
          <div className="flex items-center gap-2 text-xs text-[#66707A] uppercase tracking-wider">
            <TrendIcon className="w-3.5 h-3.5" />
            {t('benchmark.avgCostDay')}
          </div>
          <div
            className="mt-2 text-2xl font-semibold text-[#171A1D]"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            data-testid="benchmark-cost-per-day"
          >
            {currencySymbol}{comparison.benchmark.avgCostPerDay.toLocaleString()}
          </div>
          <p className="text-xs text-[#8A929B] mt-1">
            {t('benchmark.industryBenchmark', { industry: industryLabel, defaultValue: `${industryLabel} model reference` })}
          </p>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#DDE1E5]">
        <CardContent className="pt-5 pb-4 px-4">
          <div className="flex items-center gap-2 text-xs text-[#66707A] uppercase tracking-wider">
            <Clock className="w-3.5 h-3.5" />
            {t('benchmark.avgAuditDuration')}
          </div>
          <div
            className="mt-2 text-2xl font-semibold text-[#171A1D]"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            data-testid="benchmark-audit-days"
          >
            {comparison.avgAuditDays}d
          </div>
          <p className="text-xs text-[#8A929B] mt-1">{t('benchmark.certAudit')}</p>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#DDE1E5]">
        <CardContent className="pt-5 pb-4 px-4">
          <div className="flex items-center gap-2 text-xs text-[#66707A] uppercase tracking-wider">
            <DollarSign className="w-3.5 h-3.5" />
            {t('benchmark.annualMaintenance')}
          </div>
          <div
            className="mt-2 text-2xl font-semibold text-[#171A1D]"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            data-testid="benchmark-maintenance-cost"
          >
            {formatCost(comparison.certMaintenanceYearly)}
          </div>
          <p className="text-xs text-[#8A929B] mt-1">{t('benchmark.surveillanceUpkeep')}</p>
        </CardContent>
      </Card>
    </div>
  );
}
